import { Box, IconButton, useTheme } from "@mui/material";
import {
  DownloadOutlined,
  DeleteOutlined,
  DescriptionOutlined,
} from "@mui/icons-material";
import { tokens } from "../../../../theme";
import AnimatedTable, { ColumnDef } from "../AnimatedTable";
import Button from "../../../../components/common/Button";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import apiService from "../../../../service/Api";

interface EquipmentDocument {
  id: number;
  file: string;
  name: string;
  document_type: string;
  file_size: string;
  created_at: string;
  uploaded_by: string;
}

const ROWS_PER_PAGE = 10;

export default function DocumentsTab() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [documents, setDocuments] = useState<EquipmentDocument[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await apiService.get<{
        count: number;
        results: EquipmentDocument[];
      }>(
        `/api/equipments/equipment-documents/?equipment=${id}&page=${page + 1}&page_size=${ROWS_PER_PAGE}`,
      );
      setDocuments(response.data.results);
      setTotalCount(response.data.count);
    } catch {
      setDocuments([]);
    }
  }, [id, page]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);
      formData.append("equipment", id!);
      await apiService.post("/api/equipments/equipment-documents/", formData);
      await fetchDocuments();
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (docId: number) => {
    try {
      await apiService.delete(`/api/equipments/equipment-documents/${docId}/`);
      await fetchDocuments();
    } catch {
      // handle error
    }
  };

  const handleDownload = (fileUrl: string, fileName: string) => {
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = fileName;
    a.target = "_blank";
    a.click();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const totalPages = Math.ceil(totalCount / ROWS_PER_PAGE);

  const columns: ColumnDef<EquipmentDocument>[] = [
    {
      key: "file",
      label: "File",
      cellSx: { fontWeight: 500, color: theme.palette.text.primary },
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionOutlined
            sx={{ fontSize: 18, color: theme.palette.text.secondary }}
          />
          {row.name}
        </Box>
      ),
    },
    {
      key: "type",
      label: "Type",
      render: (row) => row.document_type || "—",
    },
    {
      key: "uploadedBy",
      label: "Uploaded By",
      render: (row) => row.uploaded_by || "—",
    },
    {
      key: "date",
      label: "Date",
      render: (row) => formatDate(row.created_at),
    },
    {
      key: "size",
      label: "Size",
      render: (row) => row.file_size || "—",
    },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            size="small"
            sx={{ color: theme.palette.text.secondary }}
            onClick={() => handleDownload(row.file, row.name)}
          >
            <DownloadOutlined fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: "#d32f2f" }}
            onClick={() => handleDelete(row.id)}
          >
            <DeleteOutlined fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: "16px",
        p: 3,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <input type="file" ref={fileInputRef} hidden onChange={handleUpload} />
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          sx={{
            backgroundColor: colors.primary[400],
            color: colors.primary[900],
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            "&:hover": { backgroundColor: colors.primary[50] },
          }}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </Box>

      <AnimatedTable
        columns={columns}
        rows={documents}
        getRowId={(row) => row.id}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        emptyMessage="No documents found."
      />
    </Box>
  );
}
